/**
 * Admin Home — overview dashboard with aggregated KPIs + deep-link cards into every tab.
 */
import { useEffect, useState } from 'react';
import { useAdminScopeTerms } from '../lib/adminScopeTerms';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { RouteTaskDialog } from './admin/DeptTaskShared';
import {
  Building2, Users, ShieldCheck, AlertTriangle, Trash2, CheckCircle2, Clock,
  Boxes, GraduationCap, FlaskConical, Eye, MapPin,
  ChevronRight, Bell, FileText, QrCode, Truck, Trophy, TrendingDown, TrendingUp,
  Droplets, Wind, Recycle, Sparkles, ArrowRightLeft, BarChart3, CalendarOff, Radio,
  Wrench, ClipboardList,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminHome({ onNavigate, onFilterTo }) {
  const terms = useAdminScopeTerms();
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [rankings, setRankings] = useState({ top: [], bottom: [] });
  const [loading, setLoading] = useState(true);
  const [routeOpen, setRouteOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [overviewRes, trendRes, rankRes] = await Promise.all([
        fetch(`${API}/api/ops/admin/overview`, { credentials: 'include' }),
        fetch(`${API}/api/ops/admin/score-trend?days=30`, { credentials: 'include' }),
        fetch(`${API}/api/ops/admin/property-rankings?days=30`, { credentials: 'include' }),
      ]);
      if (overviewRes.ok) setData(await overviewRes.json());
      if (trendRes.ok) {
        const t = await trendRes.json();
        setTrend(t.series || []);
      }
      if (rankRes.ok) setRankings(await rankRes.json());
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const ops = data?.operations || {};
  const bulk = data?.bulk || {};
  const billing = data?.billing || {};
  const training = data?.training || {};
  const recent = data?.recent || {};
  const score = data?.today_score;

  const go = (tab, subtype) => onNavigate?.(tab, subtype);

  return (
    <div className="space-y-6" data-testid="admin-home">
      {/* Header strip */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            COMMAND CENTER
          </h2>
          <p className="text-sm text-zinc-400">
            Live snapshot of operations, billing, training and bulk service.
            {data?.as_of && <span className="text-zinc-600"> · refreshed {new Date(data.as_of).toLocaleTimeString()}</span>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Button
            onClick={() => setRouteOpen(true)}
            className="bg-[#FFD60A] hover:bg-[#EAB308] text-black font-bold text-xs h-8"
            data-testid="ops-route-work-btn"
          >
            <ArrowRightLeft className="h-3 w-3 mr-1" /> Route to another department
          </Button>
          <CountBadge label="Properties" value={ops.properties} icon={<Building2 className="h-3 w-3" />} />
          <CountBadge label={terms.occupants} value={ops.tenants} icon={<Users className="h-3 w-3" />} />
          <CountBadge label="Staff" value={ops.staff} icon={<ShieldCheck className="h-3 w-3" />} />
        </div>
      </div>

      {/* Today's Score badge */}
      {score && <TodayScore score={score} />}

      {/* 30-day score trend sparkline */}
      {trend?.length > 0 && <ScoreSparkline series={trend} />}

      {/* Property rankings (top 3 + bottom 3) */}
      {(rankings.top?.length > 0 || rankings.bottom?.length > 0) && (
        <PropertyRankings rankings={rankings} onSelect={() => go('uber-trash')} />
      )}

      {/* Row 1: Today's Operations */}
      <Section title="TODAY'S OPERATIONS" right={<button onClick={() => go('uber-trash')} className="text-xs text-[#FFD60A] hover:underline flex items-center gap-1" data-testid="admin-home-go-uber">View Uber Trash <ChevronRight className="h-3 w-3" /></button>}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Trash placements" value={ops.today_placements ?? 0} sub="check-ins today" color="bg-blue-700" icon={<Trash2 className="h-4 w-4" />} testid="kpi-placements" />
          <Kpi label="Pickups complete" value={ops.today_pickups_complete ?? 0} sub="of today's placements" color="bg-emerald-700" icon={<CheckCircle2 className="h-4 w-4" />} testid="kpi-pickups" />
          <Kpi label="Jobs closed" value={ops.today_job_closes ?? 0} sub="properties wrapped tonight" color="bg-amber-600 text-black" icon={<Clock className="h-4 w-4" />} testid="kpi-jobs" />
          <Kpi label="Active violations" value={ops.active_violations ?? 0} sub={`+${ops.pending_review_violations ?? 0} pending review`} color="bg-red-700" icon={<AlertTriangle className="h-4 w-4" />} testid="kpi-violations" loud={(ops.pending_review_violations ?? 0) > 0} />
        </div>
      </Section>

      {/* Row 2: Quick action cards */}
      <Section title="QUICK ACCESS — JUMP TO ANY AREA">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-stretch">
          <ActionCard icon={<Eye className="h-5 w-5" />} title="Uber Trash" sub="Live transparency feed" badge={ops.today_job_closes ? `${ops.today_job_closes} closed` : null} onClick={() => go('uber-trash')} testid="card-uber-trash" />
          <ActionCard icon={<AlertTriangle className="h-5 w-5" />} title="Violations" sub="Pending review + active" badge={ops.pending_review_violations ? `${ops.pending_review_violations} pending` : null} loud onClick={() => go('reports')} testid="card-violations" />
          <ActionCard icon={<Users className="h-5 w-5" />} title="Staffing Agents" sub="Role · region · schedule (read-only)" onClick={() => go('ops-staffing')} testid="card-staffing" />
          <ActionCard icon={<Building2 className="h-5 w-5" />} title="Properties" sub="Contracted service package + agreements" onClick={() => go('ops-properties')} testid="card-properties" />

          <ActionCard icon={<Truck className="h-5 w-5" />} title="Fleet & Safety" sub="Vehicles + checklists" onClick={() => go('fleet-safety')} testid="card-fleet" />
          <ActionCard icon={<FileText className="h-5 w-5" />} title="Daily Reports" sub="Crew checklists" onClick={() => go('daily-reports')} testid="card-daily-reports" />
          <ActionCard icon={<QrCode className="h-5 w-5" />} title="QR Codes" sub="Unit + clock-in scans" onClick={() => go('qr-codes')} testid="card-qr" />
          <ActionCard icon={<MapPin className="h-5 w-5" />} title="Routes & Regions" sub="City groups + crew routes" onClick={() => go('routes-regions')} testid="card-routes" />

          <ActionCard icon={<GraduationCap className="h-5 w-5" />} title="Training Completions" sub={`${training.certificates_issued || 0} certificates issued (read-only)`} onClick={() => go('ops-training-completions')} testid="card-training" />
          <ActionCard icon={<BarChart3 className="h-5 w-5" />} title="Service Statistics" sub="Ops · Training · Valet & Specialty (state → region → city)" loud onClick={() => go('statistics')} testid="card-statistics" />
          <ActionCard
            icon={<Radio className="h-5 w-5" />}
            title="Emergency Ops"
            sub="Broadcasts · weather · outages"
            onClick={() => go('emergency-ops')}
            testid="card-emergency-ops"
          />
          <ActionCard icon={<FlaskConical className="h-5 w-5" />} title="Demo Tools" sub="Test every role" onClick={() => go('demo-tools')} testid="card-demo" />
        </div>
      </Section>

      {/* Row 2.5: PM Specialty Service tiles — each opens the Specialty
          Services queue pre-filtered by service type so admins see
          scope, prep checklist, crew guidance, and — critically — the
          completion Reports Branch that populates once assignments are
          done. Bulk Removal now lives here alongside the other
          specialty services so every item shares the same
          assignment → completion → report lifecycle. */}
      <Section title="PROPERTY SPECIALTY SERVICES">
        <p className="text-[11px] text-zinc-500 -mt-1 mb-2 italic">
          Each service exposes a full Reports Branch after assignment completion (photos, notes, closure timestamp).
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-stretch">
          <ActionCard
            icon={<Boxes className="h-5 w-5" />}
            title="Bulk Removal"
            sub="Policies + requests · reports"
            badge={bulk.open_requests ? `${bulk.open_requests} new` : (bulk.completed ? `${bulk.completed} reports` : null)}
            loud={bulk.open_requests > 0}
            onClick={() => go('specialty-services', 'bulk_removal')}
            testid="card-bulk"
          />          <ActionCard
            icon={<Droplets className="h-5 w-5" />}
            title="Pressure Washing"
            sub="Exterior + sidewalks · reports"
            onClick={() => go('specialty-services', 'pressure_washing')}
            testid="card-pressure-washing"
          />
          <ActionCard
            icon={<Sparkles className="h-5 w-5" />}
            title="Dumpster Treatment"
            sub="Deodorize + sanitize · reports"
            onClick={() => go('specialty-services', 'dumpster_treatment')}
            testid="card-dumpster-treatment"
          />
          <ActionCard
            icon={<Wind className="h-5 w-5" />}
            title="Window Cleaning"
            sub="Interior + exterior · reports"
            onClick={() => go('specialty-services', 'window_cleaning')}
            testid="card-window-cleaning"
          />
          <ActionCard
            icon={<Recycle className="h-5 w-5" />}
            title="Eco-Friendly Cleaning"
            sub="Green sanitation · reports"
            onClick={() => go('specialty-services', 'eco_friendly_amenities')}
            testid="card-eco-sanitation"
          />
          {/* Iter-132 · Commercial floor-care catalog + Day Porter. */}
          <ActionCard
            icon={<Sparkles className="h-5 w-5" />}
            title="Floor Care"
            sub="Routine floor cleaning · reports"
            onClick={() => go('specialty-services', 'floor_care')}
            testid="card-floor-care"
          />
          <ActionCard
            icon={<Sparkles className="h-5 w-5" />}
            title="Floor Polishing"
            sub="Restore shine · protect surfaces"
            onClick={() => go('specialty-services', 'floor_polishing')}
            testid="card-floor-polishing"
          />
          <ActionCard
            icon={<Wrench className="h-5 w-5" />}
            title="Floor Restoration"
            sub="Deep restoration · extend life"
            onClick={() => go('specialty-services', 'floor_restoration')}
            testid="card-floor-restoration"
          />
          <ActionCard
            icon={<ClipboardList className="h-5 w-5" />}
            title="Day Porter"
            sub="On-site presence · reports"
            onClick={() => go('specialty-services', 'day_porter')}
            testid="card-day-porter"
          />
        </div>
      </Section>

      {/* Row 3: Alerts & Heads-up. The Bulk Pickup summary card was
          retired here — Bulk Removal now lives inside PROPERTY SPECIALTY
          SERVICES with the same completion + Reports Branch lifecycle. */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
        <Card className="bg-zinc-900 border-zinc-700 p-4" data-testid="admin-home-alerts">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <Bell className="h-4 w-4 text-[#FFD60A]" /> Alerts & Heads-up
          </h3>
          <div className="space-y-2">
            <Stat label="Open fine alerts" value={ops.open_fine_alerts || 0} loud={ops.open_fine_alerts > 0} />
            <Stat label="Pending review violations" value={ops.pending_review_violations || 0} loud={ops.pending_review_violations > 0} />
            <Stat label="Upcoming blackouts" value={(ops.upcoming_blackouts || []).length} />
          </div>
          {(ops.upcoming_blackouts || [])[0] && (
            <p className="text-xs text-amber-400 mt-2">
              Next blackout: {ops.upcoming_blackouts[0].blackout_date} ({ops.upcoming_blackouts[0].reason})
            </p>
          )}
        </Card>
      </div>

      {/* Row 4: Recent activity — rich tables with Property · Unit · City · Region · Agent · Date/Time */}
      <Section title="RECENT ACTIVITY">
        <div className="grid grid-cols-1 gap-4">
          <RecentActivityTable
            title="Latest Violations"
            testid="recent-violations"
            columns={['Job', 'Type', 'Property', 'Unit', 'City', 'Region', 'Agent', 'Date & Time', 'Status']}
            onRowClick={(row) => onFilterTo && onFilterTo({ tab: 'violations', property_id: row._raw?.property_id })}
            rows={(recent.violations || []).map((v) => ({
              key: v.violation_id,
              _raw: v,
              job: (v.violation_id || '').slice(-6).toUpperCase() || '—',
              type: v.violation_type || 'Violation',
              propertyName: v.property_name,
              unit: v.unit_number || '—',
              city: v.city || '—',
              region: v.region_code || '—',
              agent: v.assigned_agent_name || v.reporter_name || v.created_by_name || '—',
              time: v.created_at,
              status: v.status || 'pending',
              tone: 'red',
            }))}
          />
          <RecentActivityTable
            title="Latest Placements"
            testid="recent-placements"
            columns={['Job', terms.occupant, 'Property', 'Unit', 'City', 'Region', 'Agent', 'Date & Time', 'Status']}
            onRowClick={(row) => onFilterTo && onFilterTo({ tab: 'ops-properties', property_id: row._raw?.property_id })}
            rows={(recent.placements || []).map((p) => ({
              key: p.placement_id,
              _raw: p,
              job: (p.placement_id || '').slice(-6).toUpperCase() || '—',
              type: p.tenant_name || '—',
              propertyName: p.property_name,
              unit: p.unit_number || '—',
              city: p.city || '—',
              region: p.region_code || '—',
              agent: p.picked_up_by_name || p.assigned_agent_name || '—',
              time: p.placed_at || p.created_at,
              status: p.placement_status || p.pickup_status || '—',
              tone: 'emerald',
            }))}
          />
          <RecentActivityTable
            title="Latest Specialty Services"
            testid="recent-specialty"
            columns={['Job', 'Service', 'Property', 'Unit', 'City', 'Region', 'Agent', 'Date & Time', 'Status']}
            onRowClick={(row) => onFilterTo && onFilterTo({ tab: 'specialty-services', property_id: row._raw?.property_id })}
            rows={(recent.specialty || recent.bulk_requests || []).map((s) => ({
              key: s.id || s.request_id,
              _raw: s,
              job: ((s.id || s.request_id || '')).slice(-6).toUpperCase() || '—',
              type: (s.service_type || 'specialty').replace(/_/g, ' '),
              propertyName: s.property_name,
              unit: s.unit_number || (s.item_count ? `${s.item_count} item(s)` : '—'),
              city: s.city || '—',
              region: s.region_code || '—',
              agent: (Array.isArray(s.assigned_agents) && s.assigned_agents.length
                ? s.assigned_agents.map((a) => a.name || a).join(', ')
                : s.assigned_supervisor || '—'),
              time: s.created_at || s.scheduled_date,
              status: s.status || '—',
              tone: 'purple',
            }))}
          />
        </div>
      </Section>

      {loading && <div className="text-center text-xs text-zinc-500">Loading…</div>}

      <RouteTaskDialog
        open={routeOpen}
        onClose={() => setRouteOpen(false)}
        defaultDept="hr"
        onSaved={() => setRouteOpen(false)}
      />
    </div>
  );
}

function Section({ title, right, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-zinc-400 tracking-widest">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function Kpi({ label, value, sub, color, icon, loud, testid }) {
  return (
    <div className={`${color} ${loud ? 'ring-2 ring-[#FFD60A]' : ''} rounded-lg p-3 text-white`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide opacity-80">{label}</span>
        <span className="opacity-80">{icon}</span>
      </div>
      <div className="text-3xl font-bold mt-1">{value}</div>
      {sub && <div className="text-[11px] opacity-80">{sub}</div>}
    </div>
  );
}

function ActionCard({ icon, title, sub, badge, loud, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      // Uniform dimensions across all Quick Access cards so the grid is
      // visually balanced regardless of title / sub-copy length: fixed
      // 96-pixel min-height, sub-line clamped to 2 lines, title clamped
      // to 1 line. Requested by CEO Feb 2026.
      className={`group text-left w-full h-full min-h-[96px] bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 ${loud ? 'border-[#FFD60A]' : ''} rounded-lg p-4 transition-colors flex items-start gap-3`}
    >
      <div className={`p-2 rounded ${loud ? 'bg-[#FFD60A] text-black' : 'bg-zinc-800 text-[#FFD60A] group-hover:bg-[#FFD60A] group-hover:text-black'} transition-colors shrink-0`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-bold text-white truncate">{title}</span>
          {badge && <Badge className={`shrink-0 ${loud ? 'bg-red-600' : 'bg-zinc-700'}`}>{badge}</Badge>}
        </div>
        <div className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{sub}</div>
      </div>
      <ChevronRight className="h-4 w-4 text-zinc-500 group-hover:text-[#FFD60A] transition-colors shrink-0 self-center" />
    </button>
  );
}

function Stat({ label, value, loud }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-zinc-400">{label}</span>
      <span className={`font-bold ${loud ? 'text-amber-400' : 'text-white'}`}>{value}</span>
    </div>
  );
}

function CountBadge({ label, value, icon }) {
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-1.5 text-xs text-zinc-300 flex items-center gap-1.5">
      {icon}
      <span className="font-bold text-[#FFD60A]">{value ?? 0}</span>
      <span className="text-zinc-500">{label}</span>
    </div>
  );
}

function RecentList({ title, empty, items, testid }) {
  return (
    <Card className="bg-zinc-900 border-zinc-700 p-4" data-testid={testid}>
      <h4 className="text-sm font-bold text-white mb-2">{title}</h4>
      {(!items || items.length === 0) ? (
        <p className="text-xs text-zinc-500 py-2">{empty}</p>
      ) : (
        <ul className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
          {items.map((it) => (
            <li key={it.id} className="text-xs">
              <div className="text-zinc-200">{it.primary}</div>
              <div className="text-zinc-500 flex items-center justify-between">
                <span>{it.secondary}</span>
                {it.time && <span>{new Date(it.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** RecentActivityTable — professional job-locator table used at the
 *  bottom of Admin Home for Violations, Placements, and Specialty
 *  Services. Renders Job # · Property · Unit · City · Region · Agent
 *  · Date/Time · Status columns. Fully accessible + horizontally
 *  scrollable on narrow screens. */
function RecentActivityTable({ title, testid, columns, rows, onRowClick }) {
  const toneClass = {
    red: 'border-red-500/40',
    emerald: 'border-emerald-500/40',
    purple: 'border-purple-500/40',
  };
  const firstRow = rows?.[0];
  const cardBorder = toneClass[firstRow?.tone] || 'border-zinc-700';
  return (
    <Card className={`bg-zinc-900 border ${cardBorder} p-4`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-bold text-white">{title}</h4>
        <Badge className="bg-zinc-800 text-zinc-300 text-[10px]">{rows.length} record{rows.length === 1 ? '' : 's'}</Badge>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-zinc-500 py-2 italic" data-testid={`${testid}-empty`}>
          No records yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid={`${testid}-table`}>
            <thead>
              <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-widest text-[#C9A962]">
                {columns.map((c) => (
                  <th key={c} className="text-left py-1.5 px-2 font-bold">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.key || r.job}
                  className={`border-b border-zinc-800/40 hover:bg-zinc-950/40 ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  data-testid={`${testid}-row-${r.key || r.job}`}
                >
                  <td className="py-1.5 px-2 font-mono text-[#C9A962] font-bold">#{r.job}</td>
                  <td className="py-1.5 px-2 text-white capitalize">{r.type}</td>
                  <td className="py-1.5 px-2 text-zinc-200 truncate max-w-[180px]">{r.propertyName || '—'}</td>
                  <td className="py-1.5 px-2 text-zinc-300 font-mono">{r.unit}</td>
                  <td className="py-1.5 px-2 text-zinc-300">{r.city}</td>
                  <td className="py-1.5 px-2">
                    <Badge className="bg-zinc-800 text-[#C9A962] font-mono text-[10px]">{r.region}</Badge>
                  </td>
                  <td className="py-1.5 px-2 text-zinc-300 truncate max-w-[140px]">{r.agent}</td>
                  <td className="py-1.5 px-2 text-zinc-400 font-mono">
                    {r.time ? new Date(r.time).toLocaleString([], {
                      year: '2-digit', month: '2-digit', day: '2-digit',
                      hour: '2-digit', minute: '2-digit',
                    }) : '—'}
                  </td>
                  <td className="py-1.5 px-2">
                    <Badge className={`text-[9px] font-bold ${
                      /pending|open|submitted|active/i.test(r.status) ? 'bg-amber-700 text-white'
                      : /resolved|closed|completed|picked/i.test(r.status) ? 'bg-emerald-700 text-white'
                      : /overdue|escalat|violat|failed/i.test(r.status) ? 'bg-red-700 text-white'
                      : 'bg-zinc-700 text-white'
                    }`}>
                      {String(r.status).replace(/_/g, ' ')}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function TodayScore({ score }) {
  const toneMap = {
    emerald: { ring: 'ring-emerald-500', bg: 'bg-gradient-to-r from-emerald-700 to-emerald-500', text: 'text-white', dot: 'bg-emerald-300' },
    amber:   { ring: 'ring-amber-500',   bg: 'bg-gradient-to-r from-amber-600 to-amber-400',     text: 'text-black', dot: 'bg-amber-200' },
    orange:  { ring: 'ring-orange-500',  bg: 'bg-gradient-to-r from-orange-600 to-orange-400',   text: 'text-white', dot: 'bg-orange-200' },
    red:     { ring: 'ring-red-500',     bg: 'bg-gradient-to-r from-red-700 to-red-500',         text: 'text-white', dot: 'bg-red-300' },
  };
  const t = toneMap[score.tone] || toneMap.emerald;
  return (
    <div className={`rounded-xl ${t.bg} ${t.text} p-5 ring-2 ${t.ring} flex flex-wrap items-center justify-between gap-4`} data-testid="admin-home-today-score">
      <div className="flex items-center gap-4">
        <div className="text-6xl font-black leading-none" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {score.composite}
          <span className="text-2xl font-bold opacity-70 ml-1">%</span>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest opacity-80">Today&apos;s Score</div>
          <div className="text-2xl font-bold leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {score.label.toUpperCase()}
          </div>
          <div className="text-xs opacity-90 mt-0.5">
            {score.placements_total} placements tonight
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 text-sm">
        <ScoreStat label="On-time" value={`${score.on_time_pct}%`} dot={t.dot} testid="score-on-time" />
        <ScoreStat label="Pickup completion" value={`${score.pickup_pct}%`} dot={t.dot} testid="score-pickup" />
        <ScoreStat label="Complaints today" value={score.complaints_count} dot={t.dot} testid="score-complaints" />
      </div>
    </div>
  );
}

function ScoreStat({ label, value, dot, testid }) {
  return (
    <div className="bg-black/20 backdrop-blur rounded-lg px-3 py-2 min-w-[120px]" data-testid={testid}>
      <div className="flex items-center gap-1.5">
        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
        <span className="text-[10px] uppercase tracking-widest opacity-80">{label}</span>
      </div>
      <div className="text-xl font-bold leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{value}</div>
    </div>
  );
}

function ScoreSparkline({ series }) {
  const [hover, setHover] = useState(null);
  const w = 720;
  const h = 70;
  const pad = 8;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2;
  const n = series.length;
  const xStep = innerW / Math.max(1, n - 1);

  const toneColor = {
    emerald: '#10b981', amber: '#f59e0b', orange: '#f97316', red: '#ef4444', neutral: '#52525b',
  };

  const points = series.map((s, i) => ({
    x: pad + i * xStep,
    y: pad + innerH - (Math.max(0, Math.min(100, s.composite)) / 100) * innerH,
    s,
  }));
  const pathD = points.length > 1
    ? 'M ' + points.map((p) => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' L ')
    : '';

  const avg = Math.round(series.reduce((a, b) => a + (b.composite || 0), 0) / Math.max(1, series.length));
  const last7 = series.slice(-7);
  const last7Avg = Math.round(last7.reduce((a, b) => a + (b.composite || 0), 0) / Math.max(1, last7.length));
  const trend = last7Avg - avg;

  return (
    <Card className="bg-zinc-900 border-zinc-700 p-4" data-testid="admin-home-score-trend">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-sm font-bold text-white flex items-center gap-2">
          <span className="text-[#FFD60A]">30-day Score Trend</span>
          <span className="text-xs text-zinc-400 font-normal">avg {avg}% · last 7 days {last7Avg}%
            <span className={`ml-1 ${trend > 0 ? 'text-emerald-400' : trend < 0 ? 'text-red-400' : 'text-zinc-400'}`}>
              ({trend > 0 ? '+' : ''}{trend})
            </span>
          </span>
        </div>
        <div className="text-[10px] text-zinc-500 flex items-center gap-3">
          <Legend color={toneColor.emerald} label="Excellent" />
          <Legend color={toneColor.amber} label="Good" />
          <Legend color={toneColor.orange} label="Watch" />
          <Legend color={toneColor.red} label="At risk" />
        </div>
      </div>

      <div className="relative">
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ maxHeight: 90 }} preserveAspectRatio="none" data-testid="score-sparkline-svg">
          {/* gridline at 75% */}
          <line x1={pad} x2={w - pad} y1={pad + innerH * 0.25} y2={pad + innerH * 0.25} stroke="#27272a" strokeDasharray="3 3" />
          {pathD && <path d={pathD} fill="none" stroke="#FFD60A" strokeWidth="1.5" strokeOpacity="0.6" />}
          {points.map((p) => (
            <g key={p.s.date}>
              <circle
                cx={p.x}
                cy={p.y}
                r={hover?.date === p.s.date ? 5 : 3}
                fill={toneColor[p.s.tone] || '#52525b'}
                stroke="#0a0a0a"
                strokeWidth="1"
                onMouseEnter={() => setHover(p.s)}
                onMouseLeave={() => setHover(null)}
                style={{ cursor: 'pointer', transition: 'r 0.1s' }}
              />
            </g>
          ))}
        </svg>
        {hover && (
          <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-black/90 text-white text-xs rounded px-2 py-1 pointer-events-none whitespace-nowrap" data-testid="score-sparkline-tooltip">
            <strong>{hover.date}</strong> · {hover.composite}% · {hover.label}
            {hover.placements > 0 && <span className="text-zinc-400"> · {hover.placements} placements</span>}
            {hover.complaints > 0 && <span className="text-red-300"> · {hover.complaints} complaints</span>}
          </div>
        )}
      </div>

      <div className="flex justify-between text-[10px] text-zinc-500 mt-1 px-1">
        <span>{series[0]?.date}</span>
        <span>{series[Math.floor(n / 2)]?.date}</span>
        <span>{series[n - 1]?.date}</span>
      </div>
    </Card>
  );
}

function Legend({ color, label }) {
  return (
    <span className="flex items-center gap-1">
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

function PropertyRankings({ rankings, onSelect }) {
  const top = rankings.top || [];
  const bottom = rankings.bottom || [];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="admin-home-rankings">
      <RankingPanel
        title="Top performers"
        subtitle="Best 30-day scores"
        icon={<Trophy className="h-5 w-5" />}
        accent="from-emerald-700 to-emerald-500"
        rows={top}
        onSelect={onSelect}
        testid="rank-top"
      />
      <RankingPanel
        title="Watch list"
        subtitle="Trailing 30-day scores"
        icon={<TrendingDown className="h-5 w-5" />}
        accent="from-red-700 to-red-500"
        rows={bottom}
        onSelect={onSelect}
        testid="rank-bottom"
        loud
      />
    </div>
  );
}

function RankingPanel({ title, subtitle, icon, accent, rows, onSelect, testid, loud }) {
  return (
    <Card className={`bg-zinc-900 border-zinc-700 ${loud ? 'border-red-700/50' : ''} p-4`} data-testid={testid}>
      <div className={`bg-gradient-to-r ${accent} text-white rounded-lg p-3 mb-3 flex items-center gap-2`}>
        <span className="bg-black/30 rounded p-1.5">{icon}</span>
        <div>
          <div className="text-sm font-bold">{title}</div>
          <div className="text-[11px] opacity-90">{subtitle}</div>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-zinc-500 py-3 text-center">Not enough data yet — add more service days.</p>
      ) : (
        <ul className="space-y-2" data-testid={`${testid}-list`}>
          {rows.map((r, idx) => <RankingRow key={r.property_id} idx={idx + 1} row={r} onSelect={onSelect} />)}
        </ul>
      )}
    </Card>
  );
}

function RankingRow({ idx, row, onSelect }) {
  const toneText = {
    emerald: 'text-emerald-400',
    amber: 'text-amber-400',
    orange: 'text-orange-400',
    red: 'text-red-400',
    neutral: 'text-zinc-400',
  };
  return (
    <li
      onClick={onSelect}
      className="flex items-center justify-between bg-zinc-800/60 hover:bg-zinc-800 border border-zinc-700 rounded-lg p-3 cursor-pointer transition-colors"
      data-testid={`rank-row-${row.property_id}`}
    >
      <div className="flex items-center gap-3">
        <span className="text-2xl font-bold text-zinc-500" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{idx}</span>
        <div>
          <div className="text-sm font-semibold text-white">{row.property_name}</div>
          <div className="text-[11px] text-zinc-500">
            {row.placements} placements · {row.on_time_pct}% on-time · {row.complaints || 0} complaint{row.complaints === 1 ? '' : 's'}
          </div>
        </div>
      </div>
      <div className="text-right">
        <div className={`text-2xl font-black ${toneText[row.tone] || 'text-white'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {row.composite ?? '—'}<span className="text-sm opacity-70">%</span>
        </div>
        <div className="text-[10px] uppercase tracking-widest opacity-60 text-zinc-400">{row.label}</div>
      </div>
    </li>
  );
}
